// IMPORTANT: Patient + Doctor auth are separate; they must not overwrite each other in localStorage.
// Legacy keys ("token"/"refreshToken") remain supported for backwards compatibility.

const LEGACY_TOKEN_KEY = "token";
const LEGACY_REFRESH_KEY = "refreshToken";

const PATIENT_TOKEN_KEY = "patientToken";
const PATIENT_REFRESH_KEY = "patientRefreshToken";
const DOCTOR_TOKEN_KEY = "doctorToken";
const DOCTOR_REFRESH_KEY = "doctorRefreshToken";
const HOSPITAL_TOKEN_KEY = "hospitalToken";
const HOSPITAL_REFRESH_KEY = "hospitalRefreshToken";

const isDoctorUrl = (url = "") => String(url || "").startsWith("/doctor");
const isHospitalUrl = (url = "") => String(url || "").startsWith("/hospital");

/** Only treat UI as "hospital provider flow" when the route is under /hospital* (not just stale localStorage). */
const isHospitalFlowPath = () => {
  try {
    const p = String(window?.location?.pathname || "").toLowerCase();
    return p.startsWith("/hospital");
  } catch (_) {
    return false;
  }
};

const getUiRole = () => {
  try {
    const p = String(window?.location?.pathname || "").toLowerCase();
    // Path wins over stale localStorage so switching doctor ↔ hospital flows cannot hit the wrong API.
    if (p.startsWith("/hospital")) return "hospital";
    if (p.startsWith("/doctor")) return "doctor";
    const active = String(localStorage.getItem("healbid_active_provider_role") || "").toLowerCase();
    if (active === "hospital" || active === "doctor") return active;
  } catch (_) {}
  return null;
};
const mapRoleWithUiContext = (role) => {
  const r = String(role || "").toLowerCase();
  const uiRole = getUiRole();
  if (uiRole === "hospital" && r === "doctor") {
    try {
      localStorage.setItem("healbid_active_provider_role", "hospital");
    } catch (_) {}
    return "hospital";
  }
  return r;
};

const getRoleKeys = (role) => {
  const r = mapRoleWithUiContext(role);
  if (r === "doctor") return { tokenKey: DOCTOR_TOKEN_KEY, refreshKey: DOCTOR_REFRESH_KEY };
  if (r === "hospital") return { tokenKey: HOSPITAL_TOKEN_KEY, refreshKey: HOSPITAL_REFRESH_KEY };
  return { tokenKey: PATIENT_TOKEN_KEY, refreshKey: PATIENT_REFRESH_KEY };
};

const safeDecodeJwtPayload = (token) => {
  try {
    const parts = String(token || "").split(".");
    if (parts.length < 2) return null;
    const payload = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = payload + "===".slice((payload.length + 3) % 4);
    // atob exists in browsers; if not present, return null
    if (typeof atob !== "function") return null;
    return JSON.parse(atob(padded));
  } catch (_) {
    return null;
  }
};

const inferRoleFromToken = (token) => {
  const p = safeDecodeJwtPayload(token);
  const roleRaw =
    p?.role ??
    p?.type ??
    p?.userType ??
    p?.accountType ??
    p?.scope ??
    "";
  const role = String(roleRaw || "").toLowerCase();
  if (role.includes("doctor")) return "doctor";
  if (role.includes("hospital")) return "hospital";
  if (role.includes("patient") || role.includes("app")) return "patient";
  return null;
};

export const tokenManager = {
  // ---------------- Role-aware API (preferred) ----------------
  getTokenForRole(role) {
    const { tokenKey } = getRoleKeys(role);
    const direct = localStorage.getItem(tokenKey);
    if (direct) return direct;

    const legacy = localStorage.getItem(LEGACY_TOKEN_KEY);
    if (!legacy) return null;

    // Try to migrate legacy token to the right bucket by inferring role from JWT payload.
    const inferred = inferRoleFromToken(legacy);
    if (inferred) {
      const { tokenKey: inferredKey } = getRoleKeys(inferred);
      localStorage.setItem(inferredKey, legacy);
      return inferred === String(role || "").toLowerCase() ? legacy : null;
    }

    // If we can't infer, fall back to legacy behavior (better than hard failing).
    return legacy;
  },

  getRefreshTokenForRole(role) {
    const { refreshKey } = getRoleKeys(role);
    return (
      localStorage.getItem(refreshKey) ||
      localStorage.getItem(LEGACY_REFRESH_KEY)
    );
  },

  setTokensForRole(role, accessToken, refreshToken) {
    const { tokenKey, refreshKey } = getRoleKeys(role);
    if (accessToken) localStorage.setItem(tokenKey, accessToken);
    if (refreshToken) localStorage.setItem(refreshKey, refreshToken);
  },

  clearRole(role) {
    const { tokenKey, refreshKey } = getRoleKeys(role);
    localStorage.removeItem(tokenKey);
    localStorage.removeItem(refreshKey);
  },

  getTokenForRequest(url) {
    if (isHospitalUrl(url)) return this.getTokenForRole("hospital");
    if (isDoctorUrl(url) && getUiRole() === "hospital" && isHospitalFlowPath()) {
      return this.getTokenForRole("hospital");
    }
    return isDoctorUrl(url)
      ? this.getTokenForRole("doctor")
      : this.getTokenForRole("patient");
  },

  clearForRequest(url) {
    if (isHospitalUrl(url)) this.clearRole("hospital");
    else if (isDoctorUrl(url) && getUiRole() === "hospital" && isHospitalFlowPath()) {
      this.clearRole("hospital");
    } else if (isDoctorUrl(url)) this.clearRole("doctor");
    else this.clearRole("patient");
  },

  normalizeRequestUrl(url) {
    const raw = String(url || "");
    try {
      const p = String(window?.location?.pathname || "").toLowerCase();
      if (p.startsWith("/hospital") && raw.startsWith("/doctor")) {
        return raw.replace("/doctor", "/hospital");
      }
    } catch (_) {}
    return raw;
  },

  clearAll() {
    localStorage.removeItem(PATIENT_TOKEN_KEY);
    localStorage.removeItem(PATIENT_REFRESH_KEY);
    localStorage.removeItem(DOCTOR_TOKEN_KEY);
    localStorage.removeItem(DOCTOR_REFRESH_KEY);
    localStorage.removeItem(HOSPITAL_TOKEN_KEY);
    localStorage.removeItem(HOSPITAL_REFRESH_KEY);
    localStorage.removeItem(LEGACY_TOKEN_KEY);
    localStorage.removeItem(LEGACY_REFRESH_KEY);
    localStorage.removeItem("healbid_active_provider_role");
  },

  setActiveProviderRole(role) {
    const r = String(role || "").toLowerCase();
    if (r !== "doctor" && r !== "hospital") return;
    try {
      localStorage.setItem("healbid_active_provider_role", r);
    } catch (_) {}
  },

  getActiveProviderRole() {
    return getUiRole() || (this.getTokenForRole("hospital") ? "hospital" : "doctor");
  },

  // ---------------- Backwards compatible API ----------------
  // Existing code calls tokenManager.getToken()/setTokens()/clear().
  // We treat those as PATIENT by default.
  getToken() {
    return this.getTokenForRole("patient");
  },

  getRefreshToken() {
    return this.getRefreshTokenForRole("patient");
  },

  setTokens(accessToken, refreshToken) {
    this.setTokensForRole("patient", accessToken, refreshToken);
  },

  clear() {
    this.clearAll();
  },
};